import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmPasswordReset } from '@/utils/auth-pb';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { PasswordInput } from '@/components/ui/password-input';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import LocalizedLink from '@/components/shared/LocalizedLink';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useLocalizedNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const passwordsMatch = password === confirmPassword;
  const passwordError = confirmPassword.length > 0 && !passwordsMatch;

  useEffect(() => {
    if (!token) {
      setError(t('resetPassword.invalidLinkMessage'));
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('resetPassword.passwordTooShort'));
      return;
    }

    if (!passwordsMatch) {
      setError(t('resetPassword.passwordsMismatch'));
      return;
    }

    setIsLoading(true);

    try {
      await confirmPasswordReset(token, password);
      setIsSuccess(true);
    } catch (err) {
      setError((err as { message: string }).message || t('resetPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Недействительная ссылка
  if (!token) {
    return (
      <AuthLayout title={t('resetPassword.invalidLinkTitle')}>
        <div className="flex flex-col items-center py-8">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#fff0f0]">
            <AlertCircle className="h-8 w-8 text-[#ff5b63]" />
          </div>
          <p className="mb-8 text-center text-[16px] text-[#565858]">
            {t('resetPassword.invalidLinkMessage')}
          </p>

          <LocalizedLink
            to="/forgot-password"
            className="mb-4 flex h-[52px] w-full items-center justify-center rounded-[12px] bg-[#2197f1] text-[16px] font-semibold text-white transition-colors hover:bg-[#1a7fd4]"
          >
            {t('resetPassword.requestNewLink')}
          </LocalizedLink>

          <LocalizedLink
            to="/login"
            className="text-[14px] text-[#2197f1] hover:underline"
          >
            {t('resetPassword.backToLogin')}
          </LocalizedLink>
        </div>
      </AuthLayout>
    );
  }

  // Успешный сброс
  if (isSuccess) {
    return (
      <AuthLayout title={t('resetPassword.successTitle')}>
        <div className="flex flex-col items-center py-8">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#e8f8e8]">
            <CheckCircle className="h-8 w-8 text-[#4caf50]" />
          </div>
          <p className="mb-8 text-center text-[16px] text-[#565858]">
            {t('resetPassword.successMessage')}
          </p>

          <button
            onClick={() => navigate('/login')}
            className="h-[52px] w-full rounded-[12px] bg-[#2197f1] text-[16px] font-semibold text-white transition-colors hover:bg-[#1a7fd4]"
          >
            {t('resetPassword.goToLogin')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Форма сброса
  return (
    <AuthLayout title={t('resetPassword.title')}>
      <div className="flex flex-col items-center">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#e8f4fd]">
          <Lock className="h-8 w-8 text-[#2197f1]" />
        </div>
        <p className="mb-8 text-center text-[16px] text-[#565858]">
          {t('resetPassword.description')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="rounded-[12px] bg-[#fff0f0] p-4 text-[14px] text-[#ff5b63]">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <label
            htmlFor="password"
            className="block text-[14px] font-medium text-[#0d0d0d]"
          >
            {t('resetPassword.passwordLabel')}
          </label>
          <PasswordInput
            id="password"
            placeholder={t('resetPassword.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="confirmPassword"
            className="block text-[14px] font-medium text-[#0d0d0d]"
          >
            {t('resetPassword.confirmPasswordLabel')}
          </label>
          <PasswordInput
            id="confirmPassword"
            placeholder={t('resetPassword.confirmPasswordPlaceholder')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isLoading}
            error={passwordError}
          />
          {passwordError && (
            <p className="text-[12px] text-[#ff5b63]">{t('resetPassword.passwordsMismatch')}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="mt-6 h-[52px] w-full rounded-[12px] bg-[#2197f1] text-[16px] font-semibold text-white transition-colors hover:bg-[#1a7fd4] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? t('resetPassword.submitting') : t('resetPassword.submit')}
        </button>

        <p className="pt-4 text-center">
          <LocalizedLink
            to="/login"
            className="text-[14px] text-[#2197f1] hover:underline"
          >
            {t('resetPassword.backToLogin')}
          </LocalizedLink>
        </p>
      </form>
    </AuthLayout>
  );
}
